// Import internal
import { Common } from '@data/models/common.model';
import { Embedded, EmbeddedFactory, EmbeddedTribe, EmbeddedUser, toFactoryEmbedded, toTribeEmbedded, toUserEmbedded, toUsersEmbedded } from './embed.model';
import { Link } from './link.model';
export type embed = keyof EmbedProperties;

export interface EmbedProperties {
    $createdBy: EmbeddedUser;
    $updatedBy: EmbeddedUser;
    $deletedBy: EmbeddedUser;
    $leaders: EmbeddedUser[];
    $operationalLeaders: EmbeddedUser[];
    $tribe: EmbeddedTribe;
    $factory: EmbeddedFactory;
}

export type EmbedCell<T extends embed> = Cell & Partial<Pick<EmbedProperties, T>>;

export const configuration: { [K in embed]: { embed: string; transform: (project: Cell, embedded: Embedded) => EmbedProperties[K] } } = {
    $createdBy: {
        embed: 'users',
        transform: (data: Cell, embedded: Embedded): EmbeddedUser => toUserEmbedded(data, 'createdBy', embedded),
    },
    $updatedBy: {
        embed: 'users',
        transform: (data: Cell, embedded: Embedded): EmbeddedUser => toUserEmbedded(data, 'updatedBy', embedded),
    },
    $deletedBy: {
        embed: 'users',
        transform: (data: Cell, embedded: Embedded): EmbeddedUser => toUserEmbedded(data, 'deletedBy', embedded),
    },
    $leaders: {
        embed: 'users',
        transform: (data: Cell, embedded: Embedded): EmbeddedUser[] => toUsersEmbedded(data, 'leaders', embedded),
    },
    $operationalLeaders: {
        embed: 'users',
        transform: (data: Cell, embedded: Embedded): EmbeddedUser[] => toUsersEmbedded(data, 'operationalLeaders', embedded),
    },
    $tribe: {
        embed: 'tribes',
        transform: (data: Cell, embedded: Embedded): EmbeddedTribe => toTribeEmbedded(data, 'tribe', embedded),
    },
    $factory: {
        embed: 'factories',
        transform: (data: Cell, embedded: Embedded): EmbeddedFactory => toFactoryEmbedded(data, 'factory', embedded),
    }
};

export class Cell extends Common {
    declare public id: string;
    public name: string;
    public description: string;
    public color: string;
    public avatarInitials?: string;
    public tribe: string;
    public analyticsCode: string;
    public type: CellType;
    public leaders: string[];
    public operationalLeaders: string[];
    public factory: string;
    public costCenter: string;
    public avatar: string;
    public supportPhone: string;
    public serviceStartHours: string;
    public serviceEndHours: string;
    public links: Link[];
    declare public tags: string[];

    constructor(data: any) {
        super(data);
        this.id = data.id;
        this.name = data.name;
        this.description = data.description;
        this.color = data.color;
        this.tribe = data.tribe;
        this.analyticsCode = data.analyticsCode;
        this.type = data.type;
        this.leaders = data.leaders ?? [];
        this.operationalLeaders = data.operationalLeaders ?? [];
        this.avatar = data.avatar;
        this.supportPhone = data.supportPhone;
        this.serviceStartHours = data.serviceStartHours;
        this.serviceEndHours = data.serviceEndHours;
        this.links = data.links ?? [];
        this.tags = data.tags ?? [];
        this.costCenter = data.costCenter;
        this.factory = data.factory;
        if (data.avatarInitials) {
            this.avatarInitials = data.avatarInitials;
        }
    }

    public static toCustomCell<T extends embed>(data: Cell, embedded: Embedded, customFields: T[]): EmbedCell<T> {
        const customData = customFields.reduce((acc, field: embed) => {
            acc[field] = configuration[field].transform(data, embedded);
            return acc;
        }, {});
        return { ...new Cell(data), ...customData } as EmbedCell<T>;
    }

    public isSupportOpen(): boolean {
        const currentMinute = (new Date().getMinutes() / 60);
        const current = new Date().getHours() + currentMinute;
        const end = Number(this.serviceEndHours.split(':')[0]) + (Number(this.serviceEndHours.split(':')[1]) / 60);
        const start = Number(this.serviceStartHours.split(':')[0]) + (Number(this.serviceStartHours.split(':')[1] === '30' ? 0.5 : 0) / 60);

        if (start <= end) {
            return start <= current && current < end;
        } else {
            return current >= start || current < end;
        }
    }

}

export enum CellType {
    UNKNOWN = 'UNKNOWN',
    SERVICE_CENTER = 'SERVICE_CENTER',
    BUSINESS = 'BUSINESS',
    SEGMENT = 'SEGMENT',
}
